import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "./useApi";
import {
  acceptRentalAgreementApi,
  rentalAgreementApi,
  WelcomeBookApi,
} from "../utils/constants";

export interface RentalAgreementDocument {
  version: string;
  language: string;
  title: string;
  body: string;
  consentLabel: string;
  ctaLabel: string;
  /**
   * MS-581 — the guest has accepted this agreement. This is the field to branch on; the two
   * timestamp fields are the ones to display. They can be null while this is true, which is
   * why they are read separately rather than derived from it.
   */
  isSigned: boolean;
  /** MS-581 — KSA date of acceptance, already formatted. Null when unsigned. */
  acceptedAtDate: string | null;
  /** MS-581 — KSA clock time of acceptance, already formatted. Null when unsigned. */
  acceptedAtTime: string | null;
}

/**
 * MS-533 — loads the rental agreement for one reservation and records consent.
 *
 * Deliberately does NOT retry. Both failure modes — the property has no verified tourism
 * permit, or nothing is published — are terminal states retrying cannot resolve, and a guest
 * waiting on a legal document should see the reason rather than a spinner running backoff.
 */
export const useRentalAgreement = (reservationId: string, enabled: boolean) => {
  const { apiPrivate } = useApi();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [rentalAgreementApi, reservationId],
    queryFn: async () => {
      const response: { data: any } = await apiPrivate.get(
        `${rentalAgreementApi}?reservationId=${encodeURIComponent(reservationId)}`
      );
      return response?.data?.result as RentalAgreementDocument;
    },
    enabled: enabled && !!reservationId,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  const acceptMutation = useMutation({
    mutationFn: async () => {
      const response: { data: any } = await apiPrivate.post(
        acceptRentalAgreementApi,
        { reservationId }
      );
      return response?.data?.result;
    },
    onSuccess: () => {
      // The welcome book carries isRentalAgreementAccepted, which gates the payment section.
      queryClient.invalidateQueries({ queryKey: [WelcomeBookApi, reservationId] });
    },
  });

  // `blocked` separates "this property cannot be paid for right now" from a transient failure,
  // so the modal can drop the retry affordance instead of implying one exists.
  const status = (query.error as any)?.response?.status;
  const blocked = status === 409;

  return {
    document: query.data ?? null,
    isLoading: query.isFetching && !query.data,
    loadError: query.isError ? { blocked } : null,
    accept: acceptMutation.mutate,
    isAccepting: acceptMutation.isPending,
  };
};

/**
 * MS-591 — the read-only signed agreement page reached from the confirmation email.
 *
 * Same endpoint as the payment-page modal, different caller: the credential here is the MS-541
 * GuestAccessToken from the emailed link and there is no login. The endpoint still accepts a
 * legacy reservation id, so this does not branch on which one it was handed.
 *
 * No accept mutation is exposed, deliberately. The consent record cannot be changed from this
 * page, and the absence of the mutation is what makes that structural rather than a matter of
 * which buttons the page happens to render.
 *
 * Does not retry, for the same reason `useRentalAgreement` does not: an unknown or expired
 * token is terminal, and a guest owed a legal document should see why rather than a spinner
 * running backoff.
 */
export const useSignedAgreement = (token: string) => {
  const { apiPrivate } = useApi();

  const query = useQuery({
    queryKey: [rentalAgreementApi, "signed", token],
    queryFn: async () => {
      const response: { data: any } = await apiPrivate.get(
        `${rentalAgreementApi}?reservationId=${encodeURIComponent(token)}`
      );
      return response?.data?.result as RentalAgreementDocument;
    },
    enabled: !!token,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  return {
    document: query.data ?? null,
    isLoading: query.isFetching && !query.data,
    hasError: query.isError,
  };
};
